import { React, _ } from 'Imports';
import { useState, useEffect } from 'react';

import {
    TableBody,
    TableHead,
    Table,
    TableCell,
    TableRow,
    TableSortLabel,
    ButtonGroup,
    TablePagination,
    TableContainer,
    Button,
    Typography,
} from 'MaterialUIComponents';

import { UserRolesResponse } from '$Generated/api';
import { RolesEnum } from '$Externals/VerifyRole';

import { IUserRoleServiceInjectedProps, UserRoleService } from '$State/UserRoleFreezerService';

import { IConfigServiceInjectedProps, ConfigService } from '$State/ConfigFreezerService';

interface IUserRolePermissionsBaseProps {
    refreshRoles: (email: string) => void;
    filterValue: string;
}

type IUserRolePermissionsProps = IUserRolePermissionsBaseProps & IConfigServiceInjectedProps & IUserRoleServiceInjectedProps;

const styles = require('./UserRolePermissions.scss') as {
    descriptionContainer: string;
    usersTable: string;
    nameColumn: string;
    emailColumn: string;
    rolesColumn: string;
    roleButton: string;
    roleButtonSelected: string;
    roleButtonText: string;
    roleButtonSubtext: string;
    validationMessage: string;
};

const rowHeaders = [
    {
        id: 'name',
        label: 'User',
        style: styles.nameColumn,
        sortable: true,
    },
    {
        id: 'email',
        label: 'Email Address',
        style: styles.emailColumn,
        sortable: true,
    },
    {
        id: 'roles',
        label: 'Roles',
        style: styles.rolesColumn,
        sortable: false,
    },
];

const _UserRolePermissions: React.FC<IUserRolePermissionsProps> = (props) => {
    const [usersList, setUsersList] = useState<UserRolesResponse[] | undefined>(undefined);
    const [usersListFiltered, setUsersListFiltered] = useState<UserRolesResponse[] | undefined>(undefined);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [messageOnRow, setMessageOnRow] = useState<{ message: string; userId: string }>({ message: '', userId: '' });

    useEffect(() => {
        getUsersList();
    }, []);

    // Apply sort and filter whenever usersList changes
    useEffect(() => {
        if (usersList) {
            applySortAndFilter();
        }
    }, [usersList, sortBy, sortOrder]);

    // Reapply filter when filterValue prop changes
    useEffect(() => {
        if (usersList) {
            applySortAndFilter();
        }
    }, [props.filterValue]);

    const applySortAndFilter = (): void => {
        if (!usersList) return;

        // First, sort the list
        const sortedList = [...usersList].sort((a, b) => {
            let res = 0;
            let aString = '';
            let bString = '';
            switch (sortBy) {
                case 'name':
                case 'email':
                    aString = a[sortBy] ? a[sortBy]! : '';
                    bString = b[sortBy] ? b[sortBy]! : '';
                    res = aString.localeCompare(bString);
                    break;
                default:
            }

            return sortOrder === 'desc' ? -1 * res : res;
        });

        // Then, apply filter
        if (props.filterValue) {
            const filteredList = sortedList.filter((row) => {
                return row?.name?.toLowerCase().includes(props.filterValue.toLowerCase());
            });
            setUsersListFiltered(filteredList);
        } else {
            setUsersListFiltered(sortedList);
        }
    };

    const getUsersList = async (): Promise<void> => {
        await props.userRoles.getUserRolePermissions();
        const { getUserRolePersmissionsResults } = props.userRoles.getState();

        setUsersList(getUserRolePersmissionsResults.data || []);
        // Sort and filter will be applied automatically via useEffect
    };

    const handleChangePage = async (event: any, newPage: number): Promise<void> => {
        setPage(newPage);
    };

    const handleChangePagination = async (event: any, newPage: number): Promise<void> => {
        setPage(newPage - 1);
    };

    const handleChangeRowsPerPage = (event: any): void => {
        setRowsPerPage(event.target.value);
        setPage(0);
    };

    const handleRequestSort = (field: string): void => {
        const isDesc = sortBy === field && sortOrder === 'asc';

        setSortBy(field);
        setSortOrder(isDesc ? 'desc' : 'asc');
    };

    const onClickRoleButton = async (
        selectedRole: RolesEnum,
        userId: string,
        userEmail: string,
        roles: string[],
        rowIndex: number,
    ): Promise<void> => {
        let roleIds = roles.map((id) => parseInt(id));
        const selectedRoleId = selectedRole.toString();

        //First 3 conditions were created to allow multiple selection between VideoEventEdit and VideoRecallEdit
        if (
            selectedRole == RolesEnum.videoEventEdit &&
            roles.indexOf(selectedRoleId) === -1 &&
            roles.indexOf(RolesEnum.videoRecallEdit.toString()) !== -1
        ) {
            roleIds.push(parseInt(selectedRoleId));
        } else if (
            selectedRole == RolesEnum.videoRecallEdit &&
            roles.indexOf(selectedRoleId) === -1 &&
            roles.indexOf(RolesEnum.videoEventEdit.toString()) !== -1
        ) {
            roleIds.push(parseInt(selectedRoleId));
        } else if (
            //If user already has both roles, clicking either button will remove the selected role
            (selectedRole == RolesEnum.videoEventEdit || selectedRole == RolesEnum.videoRecallEdit) &&
            roles.indexOf(RolesEnum.videoEventEdit.toString()) !== -1 &&
            roles.indexOf(RolesEnum.videoRecallEdit.toString()) !== -1
        ) {
            roleIds = selectedRole == RolesEnum.videoEventEdit ? [RolesEnum.videoRecallEdit] : [RolesEnum.videoEventEdit];
        } else if (roles.indexOf(selectedRoleId) !== -1) {
            roleIds = [];
        } else {
            roleIds = [parseInt(selectedRoleId)];
        }

        const userRolesUpdate = {
            userId: userId,
            email: userEmail,
            roles: roleIds,
        };

        try {
            await props.userRoles.updateUserRoles(userRolesUpdate);

            if (selectedRole == RolesEnum.videoSuperAdmin) {
                props.refreshRoles(userEmail);
            }
            await getUsersList();
        } catch (ex) {
            const errorMessage = await ex.text().then(function (text: string) {
                return text;
            });

            setMessageOnRow({ message: errorMessage, userId: userId });
            setTimeout(() => {
                setMessageOnRow({ message: '', userId: '' });
            }, 5000);
        }
    };

    return (
        <div className={styles.descriptionContainer}>
            <TableContainer>
                <Table className={styles.usersTable}>
                    <TableHead>
                        <TableRow>
                            {rowHeaders.map((head, idx) => {
                                return head.sortable ? (
                                    <TableCell key={idx} className={head.style} sortDirection={sortBy === head.id ? sortOrder : false}>
                                        <TableSortLabel
                                            active={sortBy === head.id}
                                            direction={sortOrder}
                                            onClick={(): void => handleRequestSort(head.id)}
                                        >
                                            {head.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ) : (
                                    <TableCell key={idx} className={head.style}>
                                        {head.label}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {usersListFiltered
                            ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((user: UserRolesResponse, idx) => {
                                return (
                                    <TableRow key={idx}>
                                        <TableCell className={styles.nameColumn}>{user.name}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell className={styles.rolesColumn}>
                                            <ButtonGroup>
                                                <Button
                                                    onClick={() =>
                                                        onClickRoleButton(
                                                            RolesEnum.videoEventView,
                                                            user.userId ?? '',
                                                            user.email ?? '',
                                                            user.roleIds ?? [],
                                                            idx,
                                                        )
                                                    }
                                                    className={[
                                                        styles.roleButton,
                                                        user.roleIds && user.roleIds.indexOf(RolesEnum.videoEventView.toString()) !== -1
                                                            ? styles.roleButtonSelected
                                                            : '',
                                                    ].join(' ')}
                                                    size="medium"
                                                >
                                                    <Typography className={styles.roleButtonText}>View Only</Typography>
                                                    <Typography className={styles.roleButtonSubtext}>Video Events</Typography>
                                                </Button>
                                                <Button
                                                    onClick={() =>
                                                        onClickRoleButton(
                                                            RolesEnum.videoEventEdit,
                                                            user.userId ?? '',
                                                            user.email ?? '',
                                                            user.roleIds ?? [],
                                                            idx,
                                                        )
                                                    }
                                                    className={[
                                                        styles.roleButton,
                                                        user.roleIds && user.roleIds.indexOf(RolesEnum.videoEventEdit.toString()) !== -1
                                                            ? styles.roleButtonSelected
                                                            : '',
                                                    ].join(' ')}
                                                    size="medium"
                                                >
                                                    <Typography className={styles.roleButtonText}>View + Edit</Typography>
                                                    <Typography className={styles.roleButtonSubtext}>Video Events</Typography>
                                                </Button>
                                                <Button
                                                    onClick={() =>
                                                        onClickRoleButton(
                                                            RolesEnum.videoRecallEdit,
                                                            user.userId ?? '',
                                                            user.email ?? '',
                                                            user.roleIds ?? [],
                                                            idx,
                                                        )
                                                    }
                                                    className={[
                                                        styles.roleButton,
                                                        user.roleIds && user.roleIds.indexOf(RolesEnum.videoRecallEdit.toString()) !== -1
                                                            ? styles.roleButtonSelected
                                                            : '',
                                                    ].join(' ')}
                                                    size="medium"
                                                >
                                                    <Typography className={styles.roleButtonText}>View + Edit</Typography>
                                                    <Typography className={styles.roleButtonSubtext}>Video Recall</Typography>
                                                </Button>
                                                <Button
                                                    onClick={() =>
                                                        onClickRoleButton(
                                                            RolesEnum.videoAdmin,
                                                            user.userId ?? '',
                                                            user.email ?? '',
                                                            user.roleIds ?? [],
                                                            idx,
                                                        )
                                                    }
                                                    className={[
                                                        styles.roleButton,
                                                        user.roleIds &&
                                                        user.roleIds.indexOf(RolesEnum.videoAdmin.toString()) !== -1 &&
                                                        user.roleIds.indexOf(RolesEnum.videoSuperAdmin.toString()) == -1
                                                            ? styles.roleButtonSelected
                                                            : '',
                                                    ].join(' ')}
                                                    size="medium"
                                                >
                                                    <Typography className={styles.roleButtonText}>Admin</Typography>
                                                </Button>
                                                <Button
                                                    onClick={() =>
                                                        onClickRoleButton(
                                                            RolesEnum.videoSuperAdmin,
                                                            user.userId ?? '',
                                                            user.email ?? '',
                                                            user.roleIds ?? [],
                                                            idx,
                                                        )
                                                    }
                                                    className={[
                                                        styles.roleButton,
                                                        user.roleIds && user.roleIds.indexOf(RolesEnum.videoSuperAdmin.toString()) !== -1
                                                            ? styles.roleButtonSelected
                                                            : '',
                                                    ].join(' ')}
                                                    size="medium"
                                                >
                                                    <Typography className={styles.roleButtonText}>Super Admin</Typography>
                                                </Button>
                                            </ButtonGroup>
                                            {messageOnRow.userId == user.userId && (
                                                <span className={styles.validationMessage}>{messageOnRow.message}</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                rowsPerPageOptions={[10, 20, 50]}
                count={usersListFiltered?.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </div>
    );
};

export const UserRolePermissions = ConfigService.inject(UserRoleService.inject(_UserRolePermissions));
